'use client'

import Image from 'next/image'
import { Link } from '@/i18n/navigation'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { Container } from '@/components/ui/container'
import { Eyebrow } from '@/components/ui/eyebrow'
import { Heading } from '@/components/ui/heading'
import { Text } from '@/components/ui/text'
import { Button } from '@/components/ui/button'
import { GradientText } from '@/components/ui/gradient-text'
import { Check } from '@/components/ui/check'
import BorderGlow from '@/components/ui/BorderGlow'

export function OfferHero() {
  const t = useTranslations('offer.hero')
  const trust = t.raw('trust') as string[]

  return (
    <section
      id="hero"
      className="relative flex min-h-screen items-center overflow-hidden bg-white pt-28 pb-16"
    >
      <Container>
        <div className="grid gap-12 md:grid-cols-[1.05fr_1fr] md:items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="flex flex-col gap-6"
          >
            <Eyebrow dot>{t('eyebrow')}</Eyebrow>
            <Heading as="h1" size="h1">
              {t.rich('title', {
                em: (chunks) => <GradientText as="em">{chunks}</GradientText>,
              })}
            </Heading>
            <Text size="lg" tone="muted" className="max-w-xl">
              {t('desc')}
            </Text>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link href="#booking">{t('ctaPrimary')}</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link href="#how">{t('ctaSecondary')}</Link>
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
              {trust.map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <Check size="sm" />
                  <Text size="sm" tone="muted">
                    {item}
                  </Text>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
            className="relative"
          >
            <BorderGlow
              backgroundColor="#ffffff"
              borderRadius={24}
              glowRadius={40}
              edgeSensitivity={21}
              glowColor="220 80 70"
              glowIntensity={1}
              coneSpread={25}
              colors={['#c084fc', '#f472b6', '#38bdf8']}
            >
              <div className="overflow-hidden rounded-[20px]">
                <Image
                  src="/lead-platform-hero.webp"
                  alt="Infiteq Lead Platform dashboard"
                  width={1600}
                  height={1100}
                  priority
                  sizes="(min-width: 768px) 50vw, 100vw"
                  className="h-auto w-full"
                />
              </div>
            </BorderGlow>
          </motion.div>
        </div>
      </Container>

      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-20 h-[500px] w-[500px] rounded-full bg-[radial-gradient(circle_at_center,rgba(43,90,255,0.2),transparent_60%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 bottom-0 h-[400px] w-[400px] rounded-full bg-[radial-gradient(circle_at_center,rgba(160,32,200,0.15),transparent_60%)]"
      />
    </section>
  )
}
